import { Helmet } from "react-helmet-async";
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Footer from '../components/Footer';
import SimpleLoader from '../components/SimpleLoader';

const Home = () => {
  const { user } = useAuth();
  const [membershipStatus, setMembershipStatus] = useState({
    freeListingsUsed: 0,
    freeListingsAvailable: 3,
    freeListingsRemaining: 3,
    membershipListings: 0,
    hasActiveMembership: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadMembershipStatus();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadMembershipStatus = async () => {
    try {
      const response = await api.get('/membership/status');
      setMembershipStatus(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>VestrioZ - Rent, Share & Sell Fashion and Jewellery</title>
        <meta
          name="description"
          content="VestrioZ is India's smart fashion sharing platform to rent, buy and sell premium clothing and jewellery. Earn from your wardrobe or find the perfect outfit."
        />
        <meta name="keywords" content="fashion rental, jewellery rental, rent clothes India, sustainable fashion, VestrioZ" />
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/VESTRIOZ.png" />
      </Helmet>
      <div className="min-h-screen" style={{ background: '#F6F1EB' }}>
        <div className="max-w-6xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#222831' }}>
              Welcome to VestrioZ
            </h1>
            <p className="text-xl max-w-2xl mx-auto mb-6" style={{ color: '#393E46' }}>
              What would you like to do today? Choose your path to sharing style and sustainable fashion.
            </p>
          </div>

          {/* Main Options */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            {/* Rent Items */}
            <Link to="/rent-items" className="block group">
              <div className="backdrop-blur-sm rounded-2xl shadow-xl p-8 card-hover border h-full" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="text-center">
                  <div className="w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-2xl transition-all duration-300 " style={{ background: '#393E46' }}>
                    <i className="fas fa-search text-4xl text-white"></i>
                  </div>
                  <h2 className="text-3xl font-bold mb-4" style={{ color: '#222831' }}>Rent or Sell Items</h2>
                  <p className="mb-6 leading-relaxed" style={{ color: '#393E46' }}>
                    Browse and rent amazing clothing and jewellery from other users. Find the perfect outfit and accessories for any occasion at affordable prices.
                  </p>
                  <div className="grid grid-cols-1 gap-3 mb-8">
                    <div className="flex items-center justify-center text-sm" style={{ color: '#393E46' }}>
                      <i className="fas fa-filter mr-2" style={{ color: '#948979' }}></i> Advanced Filters
                    </div>
                    <div className="flex items-center justify-center text-sm" style={{ color: '#393E46' }}>
                      <i className="fas fa-map-marker-alt mr-2" style={{ color: '#948979' }}></i> Location Based Search
                    </div>
                    <div className="flex items-center justify-center text-sm" style={{ color: '#393E46' }}>
                      <i className="fas fa-tags mr-2" style={{ color: '#948979' }}></i> Best Prices Guaranteed
                    </div>
                  </div>
                  <div className="btn-primary w-full text-center">
                    <i className="fas fa-rocket mr-2"></i>Start Browsing
                  </div>
                </div>
              </div>
            </Link>

            {/* List Items */}
            <Link to="/list-items" className="block group">
              <div className="backdrop-blur-sm rounded-2xl shadow-xl p-8 card-hover border h-full" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="text-center">
                  <div className="w-24 h-24 gradient-secondary rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-2xl transition-all duration-300" style={{ background: '#393E46' }}>
                    <i className="fas fa-plus-circle text-4xl text-white"></i>
                  </div>
                  <h2 className="text-3xl font-bold mb-4" style={{ color: '#393E46' }}>List Your Items</h2>
                  <p className="mb-6 leading-relaxed" style={{ color: '#393E46' }}>
                    List your clothing and jewellery items for rent and earn money from your wardrobe. Share your style with others and make it profitable.
                  </p>
                  <div className="grid grid-cols-1 gap-3 mb-8">
                    <div className="flex items-center justify-center text-sm" style={{ color: '#393E46' }}>
                      <i className="fas fa-gift mr-2" style={{ color: '#948979' }}></i> 3 Free Listings
                    </div>
                    <div className="flex items-center justify-center text-sm" style={{ color: '#393E46' }}>
                      <i className="fas fa-calendar mr-2" style={{ color: '#948979' }}></i> 30 Days Active
                    </div>
                    <div className="flex items-center justify-center text-sm" style={{ color: '#393E46' }}>
                      <i className="fas fa-rupee-sign mr-2" style={{ color: '#948979' }}></i> Earn Money Daily
                    </div>
                  </div>
                  <div className="btn-secondary w-full text-center" style={{ background: '#393E46' }}>
                    <i className="fas fa-upload mr-2"></i>List Your Items
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Account Status for Authenticated Users */}
          {user ? (
            <div className="backdrop-blur-sm rounded-2xl shadow-xl p-8 border" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
              <h3 className="text-3xl font-bold text-center mb-8" style={{ color: '#222831' }}>
                Your Account Status
              </h3>
              {loading ? (
                <div className="text-center py-12">
                  <SimpleLoader size="lg" text="Loading account status..." />
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-3 gap-8 mb-8">
                    <div className="text-center p-6 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(223, 208, 184, 0.3) 0%, rgba(245, 241, 232, 0.5) 100%)' }}>
                      <div className="text-4xl font-bold mb-2" style={{ color: '#222831' }}>
                        {(membershipStatus.freeListingsUsed || 0)}/{membershipStatus.freeListingsAvailable || 3}
                      </div>
                      <div className="font-medium" style={{ color: '#393E46' }}>Free Listings Used</div>
                      <div className="text-sm mt-1" style={{ color: '#948979' }}>
                        {membershipStatus.freeListingsRemaining || ((membershipStatus.freeListingsAvailable || 3) - (membershipStatus.freeListingsUsed || 0))} remaining
                      </div>
                    </div>
                    <div className="text-center p-6 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(148, 137, 121, 0.2) 0%, rgba(223, 208, 184, 0.3) 100%)' }}>
                      <div className="text-4xl font-bold mb-2" style={{ color: '#948979' }}>
                        {membershipStatus.membershipListings || 0}
                      </div>
                      <div className="font-medium" style={{ color: '#393E46' }}>Membership Listings</div>
                      <div className="text-sm mt-1" style={{ color: '#948979' }}>Premium slots</div>
                    </div>
                    <div className="text-center p-6 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(57, 62, 70, 0.1) 0%, rgba(148, 137, 121, 0.2) 100%)' }}>
                      <div className={`text-2xl font-bold mb-2 ${(membershipStatus.hasActiveMembership || false) ? 'text-green-600' : 'text-red-500'
                        }`}>
                        {(membershipStatus.hasActiveMembership || false) ? 'Active' : 'Inactive'}
                      </div>
                      <div className="font-medium" style={{ color: '#393E46' }}>Membership Status</div>
                      <div className="text-sm mt-1" style={{ color: '#948979' }}>
                        {(membershipStatus.hasActiveMembership || false) ? 'Premium benefits' : 'Basic plan'}
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <Link
                      to="/membership"
                      className="btn-primary inline-flex items-center"
                    >
                      <i className="fas fa-crown mr-2"></i>Upgrade Membership
                    </Link>
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Welcome Section for Non-Authenticated Users */
            <div className="backdrop-blur-sm rounded-2xl shadow-xl p-8 border" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
              <h3 className="text-3xl font-bold text-center mb-8" style={{ color: '#222831' }}>
                Get Started Today
              </h3>
              <div className="text-center">
                <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: '#393E46' }}>
                  Join thousands of users who are already sharing their wardrobe and earning money through sustainable fashion.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4 max-w-md mx-auto">
                  <Link
                    to="/signup"
                    className="btn-primary flex-1 text-center"
                  >
                    <i className="fas fa-user-plus mr-2"></i>Sign Up Free
                  </Link>
                  <Link
                    to="/login"
                    className="btn-outline flex-1 text-center"
                  >
                    <i className="fas fa-sign-in-alt mr-2"></i>Login
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
};
export default Home;